import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { useMetricDataByWeekId } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useMetricDataByWeekId';
import AdvancedSearchRequestBuilder, {
  useAdvancedSearchRequestBuilder
} from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { useAppSelector } from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { safeDivide } from 'src/utils/app';
import { useEffect, useMemo, useRef } from 'react';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import AdvancedSearchParser from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/AdvancedSearchParser';
import { AdvancedSearchResponse } from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/types';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';

type AggregationFieldName = 'lifetimeReviewVolume' | 'childLifetimeRatingVolume' | 'parentLifetimeRatingVolume';

// Union types for dynamic field level selection
export type FieldLevel = 'child' | 'parent';

// Helper function to determine field level based on product entity
export const useFieldLevel = (): FieldLevel => {
  const productEntity = useProductEntity();
  return productEntity.isProductEntity ? 'parent' : 'child';
};

// Helper function to generate dynamic field names
const generateFieldNames = (level: FieldLevel) => ({
  weeklyOneStarRatingCount: `${level}WeeklyOneStarRatingCount` as const,
  weeklyTwoStarsRatingCount: `${level}WeeklyTwoStarsRatingCount` as const,
  weeklyThreeStarsRatingCount: `${level}WeeklyThreeStarsRatingCount` as const,
  weeklyFourStarsRatingCount: `${level}WeeklyFourStarsRatingCount` as const,
  weeklyFiveStarsRatingCount: `${level}WeeklyFiveStarsRatingCount` as const,
  lifetimeOneStarRatingCount: `${level}LifetimeOneStarRatingCount` as const,
  lifetimeTwoStarsRatingCount: `${level}LifetimeTwoStarsRatingCount` as const,
  lifetimeThreeStarsRatingCount: `${level}LifetimeThreeStarsRatingCount` as const,
  lifetimeFourStarsRatingCount: `${level}LifetimeFourStarsRatingCount` as const,
  lifetimeFiveStarsRatingCount: `${level}LifetimeFiveStarsRatingCount` as const,
  weeklyRatingVolume: `${level}WeeklyRatingVolume` as const,
  lifetimeRatingVolume: `${level}LifetimeRatingVolume` as const
});

// Specific interfaces for each field level
interface ChildRatingVolumeByStarData {
  weekId: string;
  childWeeklyOneStarRatingCount_sum_value: number;
  childWeeklyTwoStarsRatingCount_sum_value: number;
  childWeeklyThreeStarsRatingCount_sum_value: number;
  childWeeklyFourStarsRatingCount_sum_value: number;
  childWeeklyFiveStarsRatingCount_sum_value: number;
}

interface ParentRatingVolumeByStarData {
  weekId: string;
  parentWeeklyOneStarRatingCount_sum_value: number;
  parentWeeklyTwoStarsRatingCount_sum_value: number;
  parentWeeklyThreeStarsRatingCount_sum_value: number;
  parentWeeklyFourStarsRatingCount_sum_value: number;
  parentWeeklyFiveStarsRatingCount_sum_value: number;
}

interface ChildWeeklyDataItemAdditionalValues {
  weightedWeeklyRating_sum_value: number;
  weightedWeeklyRating_sum_count: number;
  childWeeklyRatingVolume_sum_value: number;
  childWeeklyRatingVolume_sum_count: number;
  weeklyRating_computed_value: number;
  weeklyRating_computed_count: number;
  weeklyReviewRating_computed_value?: number;
  weeklyReviewRating_computed_count?: number;
}

interface ParentWeeklyDataItemAdditionalValues {
  weightedWeeklyRating_sum_value: number;
  weightedWeeklyRating_sum_count: number;
  parentWeeklyRatingVolume_sum_value: number;
  parentWeeklyRatingVolume_sum_count: number;
  weeklyRating_computed_value: number;
  weeklyRating_computed_count: number;
  weeklyReviewRating_computed_value?: number;
  weeklyReviewRating_computed_count?: number;
}

type WeeklyDataItem<L extends FieldLevel = 'child'> = {
  value: number;
  additionalValues: L extends 'child' ? ChildWeeklyDataItemAdditionalValues : ParentWeeklyDataItemAdditionalValues;
};

export interface childLifetimeRatingVolumeByStarResponse {
  id: string;
  name: string;
  searchType: string;
  aggregations: {
    by_retailerId: {
      fieldName: string;
      fieldId: string;
      weekId: number;
      value: number;
      count: number;
      additionalValues: {
        childLifetimeOneStarRatingCount_sum_value: number;
        childLifetimeOneStarRatingCount_sum_count: number;
        childLifetimeTwoStarsRatingCount_sum_value: number;
        childLifetimeTwoStarsRatingCount_sum_count: number;
        childLifetimeThreeStarsRatingCount_sum_value: number;
        childLifetimeThreeStarsRatingCount_sum_count: number;
        childLifetimeFourStarsRatingCount_sum_value: number;
        childLifetimeFourStarsRatingCount_sum_count: number;
        childLifetimeFiveStarsRatingCount_sum_value: number;
        childLifetimeFiveStarsRatingCount_sum_count: number;
      };
      additionalMetaData: {
        retailerId: string;
        name: string;
      };
    }[];
  };
}

export interface ProductCatalogCountRatingVolumeResponse {
  data: {
    id: string;
    name: string;
    searchType: string;
    aggregations: {
      by_retailerId: {
        fieldName: string;
        fieldId: string;
        weekId: number;
        value: number;
        count: number;
        additionalValues: {
          [key: string]: number;
        };
        additionalMetaData: {
          retailerId: string;
          name: string;
        };
      }[];
    };
  }[];
}

export const STAR_RATING_RANGE = [
  { displayName: '1.0 - 1.9 Stars', range: [1, 1.999] },
  { displayName: '2.0 - 2.9 Stars', range: [2, 2.999] },
  { displayName: '3.0 - 3.9 Stars', range: [3, 3.999] },
  { displayName: '4.0 - 5.0 Stars', range: [4, 5] }
];

const RATING_VOLUME_TIER = [
  { displayName: '0 - 19 Ratings', range: [0, 19] },
  { displayName: '20 - 99 Ratings', range: [20, 99] },
  { displayName: '100 - 199 Ratings', range: [100, 199] },
  { displayName: '200 - 299 Ratings', range: [200, 299] },
  { displayName: '300 - 399 Ratings', range: [300, 399] },
  { displayName: '400 and Above', range: [400] }
];

const REVIEW_VOLUME_TIER = [
  { displayName: '0 - 19 Reviews', range: [0, 19] },
  { displayName: '20 - 99 Reviews', range: [20, 99] },
  { displayName: '100 - 199 Reviews', range: [100, 199] },
  { displayName: '200 - 299 Reviews', range: [200, 299] },
  { displayName: '300 - 399 Reviews', range: [300, 399] },
  { displayName: '400 and Above', range: [400] }
];

export const useLatestCumulativeReviewWeekId = (): {
  latestCumulativeWeekId: number | null;
  loading: boolean;
} => {
  const cancelSource = useRef(axios.CancelToken.source());
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const retailer = useAppSelector((state) => state.retailer);

  const fetchLatestWeekId = async () => {
    try {
      const response = await axios.get(
        `api/beacon/GetLatestWeekForCumulativeRatings?beaconClientId=${beaconClientId}&retailerId=${retailer.id}`
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch review latestWeekId');
      return null;
    }
  };

  const query = useQuery({
    queryKey: ['latestCumulativeReview', beaconClientId, retailer.id],
    queryFn: fetchLatestWeekId,
    retry: 0,
    staleTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    const cancelNetworkCalls = cancelSource.current.cancel;

    return () => cancelNetworkCalls();
  }, []);

  return {
    latestCumulativeWeekId: query.data?.data?.latestWeek ?? 202430,
    loading: query.isLoading
  };
};

export const useChildWeeklyRatingVolumeByStarData = () => {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const fieldLevel = useFieldLevel();
  const fieldNames = generateFieldNames(fieldLevel);

  const { data: weeklyRatingVolumeByStar, isLoading: weeklyRatingVolumeByStarLoading } = useMetricDataByWeekId({
    startWeekId: startWeek,
    endWeekId: endWeek,
    requestId: `review-${fieldLevel}WeeklyRatingVolumeByStar`,
    fields: [
      { name: fieldNames.weeklyOneStarRatingCount },
      { name: fieldNames.weeklyTwoStarsRatingCount },
      { name: fieldNames.weeklyThreeStarsRatingCount },
      { name: fieldNames.weeklyFourStarsRatingCount },
      { name: fieldNames.weeklyFiveStarsRatingCount }
    ],
    indexName: BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2
  });

  // Transform data to match expected format with dynamic field names
  const transformedData = useMemo((): ChildRatingVolumeByStarData[] => {
    if (!weeklyRatingVolumeByStar) return [];

    if (fieldLevel === 'child') {
      return (weeklyRatingVolumeByStar as ChildRatingVolumeByStarData[]).sort(
        (a, b) => Number(a.weekId) - Number(b.weekId)
      );
    } else {
      // Transform parent data to child format for backward compatibility
      return (weeklyRatingVolumeByStar as ParentRatingVolumeByStarData[])
        .map(
          (datum): ChildRatingVolumeByStarData => ({
            weekId: datum.weekId,
            childWeeklyOneStarRatingCount_sum_value: datum.parentWeeklyOneStarRatingCount_sum_value,
            childWeeklyTwoStarsRatingCount_sum_value: datum.parentWeeklyTwoStarsRatingCount_sum_value,
            childWeeklyThreeStarsRatingCount_sum_value: datum.parentWeeklyThreeStarsRatingCount_sum_value,
            childWeeklyFourStarsRatingCount_sum_value: datum.parentWeeklyFourStarsRatingCount_sum_value,
            childWeeklyFiveStarsRatingCount_sum_value: datum.parentWeeklyFiveStarsRatingCount_sum_value
          })
        )
        .sort((a, b) => Number(a.weekId) - Number(b.weekId));
    }
  }, [weeklyRatingVolumeByStar, fieldLevel]);

  return {
    data: transformedData,
    isLoading: weeklyRatingVolumeByStarLoading
  };
};

export const parseChildLifetimeRatingVolumeByStarPercentResponse = ({
  data,
}: {
  data: childLifetimeRatingVolumeByStarResponse;
  fieldLevel?: FieldLevel;
}): [string, number][] => {
  const displayNames = {
    childLifetimeOneStarRatingCount_sum_value: '1 Star',
    childLifetimeTwoStarsRatingCount_sum_value: '2 Stars',
    childLifetimeThreeStarsRatingCount_sum_value: '3 Stars',
    childLifetimeFourStarsRatingCount_sum_value: '4 Stars',
    childLifetimeFiveStarsRatingCount_sum_value: '5 Stars',
    parentLifetimeOneStarRatingCount_sum_value: '1 Stars',
    parentLifetimeTwoStarsRatingCount_sum_value: '2 Star',
    parentLifetimeThreeStarsRatingCount_sum_value: '3 Stars',
    parentLifetimeFourStarsRatingCount_sum_value: '4 Stars',
    parentLifetimeFiveStarsRatingCount_sum_value: '5 Stars',
    lifetimeOneStarReviewVolume_sum_value: '1 Star',
    lifetimeTwoStarsReviewVolume_sum_value: '2 Stars',
    lifetimeThreeStarsReviewVolume_sum_value: '3 Stars',
    lifetimeFourStarsReviewVolume_sum_value: '4 Stars',
    lifetimeFiveStarsReviewVolume_sum_value: '5 Stars'
  };

  const parser = new AdvancedSearchParser(data as unknown as AdvancedSearchResponse);

  const metrics = parser.getAggregations()?.by_retailerId?.[0]?.additionalValues ?? {};

  const totalReviewVolume = Object.entries(metrics).reduce((sum, [key, value]) => {
    if (key.endsWith('sum_value')) {
      sum += value;
    }
    return sum;
  }, 0);

  const shareOfTotal = Object.entries(metrics).reduce((accumulator, [key, value]) => {
    if (key.endsWith('sum_value')) {
      accumulator.push([displayNames?.[key] ?? '', safeDivide(value, totalReviewVolume)]);
    }
    return accumulator;
  }, []);

  return shareOfTotal;
};

export const parseChildLifetimeRatingVolumeByStarResponse = ({
  data,
  fieldLevel = 'child'
}: {
  data: childLifetimeRatingVolumeByStarResponse;
  fieldLevel?: FieldLevel;
}): [string, number][] => {
  const displayNames = {
    childLifetimeOneStarRatingCount_sum_value: '1 Star',
    childLifetimeTwoStarsRatingCount_sum_value: '2 Stars',
    childLifetimeThreeStarsRatingCount_sum_value: '3 Stars',
    childLifetimeFourStarsRatingCount_sum_value: '4 Stars',
    childLifetimeFiveStarsRatingCount_sum_value: '5 Stars',
    parentLifetimeOneStarRatingCount_sum_value: '1 Star',
    parentLifetimeTwoStarsRatingCount_sum_value: '2 Stars',
    parentLifetimeThreeStarsRatingCount_sum_value: '3 Stars',
    parentLifetimeFourStarsRatingCount_sum_value: '4 Stars',
    parentLifetimeFiveStarsRatingCount_sum_value: '5 Stars',
    lifetimeOneStarReviewVolume_sum_value: '1 Star',
    lifetimeTwoStarsReviewVolume_sum_value: '2 Stars',
    lifetimeThreeStarsReviewVolume_sum_value: '3 Stars',
    lifetimeFourStarsReviewVolume_sum_value: '4 Stars',
    lifetimeFiveStarsReviewVolume_sum_value: '5 Stars'
  };

  const parser = new AdvancedSearchParser(data as unknown as AdvancedSearchResponse);

  const metrics = parser.getAggregations()?.by_retailerId?.[0]?.additionalValues ?? {};

  const ratingVolumeByStarRating = Object.entries(metrics).reduce((accumulator, [key, value]) => {
    if (key.endsWith('sum_value')) {
      accumulator.push([displayNames?.[key] ?? '', value]);
    }
    return accumulator;
  }, []);

  return ratingVolumeByStarRating;
};

export const useChildLifetimeRatingVolumeByStarData = () => {
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const fieldLevel = useFieldLevel();
  const fieldNames = generateFieldNames(fieldLevel);

  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const aggregationByLifetimeRatingVolume = new AggregationBuilder('retailerId')
    .addAggregationField('Life Time One Star Rating Volume', fieldNames.lifetimeOneStarRatingCount, 'sum', false)
    .addAggregationField('Life Time Two Star Rating Volume', fieldNames.lifetimeTwoStarsRatingCount, 'sum', false)
    .addAggregationField('Life Time Three Star Rating Volume', fieldNames.lifetimeThreeStarsRatingCount, 'sum', false)
    .addAggregationField('Life Time Four Star Rating Volume', fieldNames.lifetimeFourStarsRatingCount, 'sum', false)
    .addAggregationField('Life Time Five Star Rating Volume', fieldNames.lifetimeFiveStarsRatingCount, 'sum', false)
    .setSortDirection(null)
    .setGroupByFieldName('retailerId')
    .build();

  const requestByLifetimeRatingVolume = new AdvancedSearchRequestBuilder(
    `mainEntityMetrics-client-${beaconClientId}-${fieldLevel}LifetimeRatingVolumeByStar`,
    'beacon-ratings-reviews-metrics-v2'
  )
    .setPageNumber(1)
    .setPageSize(10000)
    .setRetailerId(retailer.id)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .addConditionTermFilter('retailerId', 'should', [retailer.id])
    .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
    .setSearchBy('parent')
    .addAggregation(aggregationByLifetimeRatingVolume)
    .addSortField('Life Time Rating Volume', fieldNames.lifetimeRatingVolume, 'sum', false)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const request = requestByLifetimeRatingVolume.build();

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: `${fieldLevel}LifetimeRatingVolumeByStar-columnChart`,
    requestBody: [request],
    queryKeys: [request]
  });

  return { isLoading, data };
};

export const parseCumulativeProductCatalogCountRatingVolumeResponse = ({
  data,
  comparisonEndWeek,
  metricName
}: {
  data: ProductCatalogCountRatingVolumeResponse;
  comparisonEndWeek?: number;
  metricName: 'Ratings' | 'Reviews';
}) => {
  const metricTier = metricName === 'Reviews' ? REVIEW_VOLUME_TIER : RATING_VOLUME_TIER;
  const currentProductCatalogCountDataset = data?.data.map((datum, index) => {
    const currentProductCatalogCount =
      datum?.aggregations?.by_retailerId?.[0]?.additionalValues?.stacklineSku_cardinality_count ?? 0;

    return [metricTier[index].displayName, currentProductCatalogCount];
  });

  const compareProductCatalogCountDataset = data?.data.map((datum, index) => {
    const compareProductCatalogCount =
      datum?.aggregations?.by_retailerId?.[0]?.additionalValues?.[
      `stacklineSku_cardinality_count_${comparisonEndWeek}_${comparisonEndWeek}`
      ] ?? 0;

    return [metricTier[index].displayName, compareProductCatalogCount];
  });

  return { currentData: currentProductCatalogCountDataset, compareData: compareProductCatalogCountDataset };
};

// This can be used in both ratings tab and review tab for "Product catalog count by Cumulative data fetch"
// childLifetimeRatingVolume for ratings tab, lifetimeReviewVolume for reviews tab
export const useProductCatalogCountRatingData = (fieldName = RatingsAndReviewMetric.CHILD_LIFETIME_RATING_VOLUME) => {
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);

  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const requestArray = RATING_VOLUME_TIER.map(({ range: [min, max] }) => {
    const aggregationByLifeTimeMetricVolume = new AggregationBuilder('retailerId')
      .addAggregationField('Stackline SKU', 'stacklineSku', 'cardinality', false)
      .setSortDirection(null)
      .setGroupByFieldName('retailerId')
      .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
      .addComparisonRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek)
      .build();
    const requestByLifetimeVolume = new AdvancedSearchRequestBuilder(
      `mainEntityMetrics-client-${beaconClientId}-productCatalogCount${fieldName}`,
      'beacon-ratings-reviews-metrics-v2'
    )
      .setPageNumber(1)
      .setPageSize(10000)
      .setReturnAdditionalMetadata(false)
      .setRetailerId(retailer.id)
      .setPeriod('year')
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .addConditionTermFilter('retailerId', 'should', [retailer.id])
      .setSearchBy('parent')
      .addAggregation(aggregationByLifeTimeMetricVolume)
      .addSortField('Stackline SKU', 'stacklineSku', 'cardinality', false)
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters);

    requestByLifetimeVolume.addConditionRangeFilter(fieldName, min, max);
    return requestByLifetimeVolume.build();
  });

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: `productCatalogCount${fieldName}-columnChart`,
    requestBody: requestArray,
    queryKeys: requestArray
  });

  return { isLoading, data };
};

export const parseWeeklyProductCatalogCountData = ({
  data,
  metricName
}: {
  data: AdvancedSearchResponse;
  metricName: 'Reviews' | 'Ratings';
}) => {
  const parseSkuArray = (skuArray: WeeklyDataItem[]) => {
    const volumeCounts = {
      volume0To19: 0,
      volume20To99: 0,
      volume100To199: 0,
      volume200To299: 0,
      volume300To399: 0,
      volume400ToAbove: 0
    };

    skuArray.forEach((sku) => {
      const { value } = sku;
      if (value >= 400) {
        volumeCounts.volume400ToAbove += 1;
      } else if (value >= 300) {
        volumeCounts.volume300To399 += 1;
      } else if (value >= 200) {
        volumeCounts.volume200To299 += 1;
      } else if (value >= 100) {
        volumeCounts.volume100To199 += 1;
      } else if (value >= 20) {
        volumeCounts.volume20To99 += 1;
      } else {
        volumeCounts.volume0To19 += 1;
      }
    });

    return volumeCounts;
  };

  const currentSkuArray = data?.[0]?.aggregations?.by_stacklineSku || [];
  const compareSkuArray = data?.[1]?.aggregations?.by_stacklineSku || [];

  const currentVolumeCounts = parseSkuArray(currentSkuArray);
  const compareVolumeCounts = parseSkuArray(compareSkuArray);
  return {
    currentData: [
      [`0 - 19 ${metricName}`, currentVolumeCounts.volume0To19],
      [`20 - 99 ${metricName}`, currentVolumeCounts.volume20To99],
      [`100 - 199 ${metricName}`, currentVolumeCounts.volume100To199],
      [`200 - 299 ${metricName}`, currentVolumeCounts.volume200To299],
      [`300 - 399 ${metricName}`, currentVolumeCounts.volume300To399],
      ['400 and Above', currentVolumeCounts.volume400ToAbove]
    ],
    compareData: [
      [`0 - 19 ${metricName}`, compareVolumeCounts.volume0To19],
      [`20 - 99 ${metricName}`, compareVolumeCounts.volume20To99],
      [`100 - 199 ${metricName}`, compareVolumeCounts.volume100To199],
      [`200 - 299 ${metricName}`, compareVolumeCounts.volume200To299],
      [`300 - 399 ${metricName}`, compareVolumeCounts.volume300To399],
      ['400 and Above', compareVolumeCounts.volume400ToAbove]
    ]
  };
};

export const parseWeeklyProductCatalogCountRatingData = ({
  data,
  field = 'rating'
}: {
  data: AdvancedSearchResponse;
  field: 'rating' | 'review';
}) => {
  const responseParseKey = {
    rating: 'weeklyRating_computed_value',
    review: 'weeklyReviewRating_computed_value'
  };

  const parseSkuArray = (skuArray: WeeklyDataItem[]) => {
    const volumeCounts = {
      volumeLessThan2: 0,
      volumeLessThan3: 0,
      volumeLessThan4: 0,
      volumeLessThan5: 0
    };

    skuArray.forEach((sku) => {
      const value = sku?.additionalValues?.[responseParseKey[field]];
      if (value >= 4) {
        volumeCounts.volumeLessThan5 += 1;
      } else if (value >= 3) {
        volumeCounts.volumeLessThan4 += 1;
      } else if (value >= 2) {
        volumeCounts.volumeLessThan3 += 1;
      } else {
        volumeCounts.volumeLessThan2 += 1;
      }
    });

    return volumeCounts;
  };

  const currentSkuArray = data?.[0]?.aggregations?.by_stacklineSku || [];
  const compareSkuArray = data?.[1]?.aggregations?.by_stacklineSku || [];

  const currentVolumeCounts = parseSkuArray(currentSkuArray);
  const compareVolumeCounts = parseSkuArray(compareSkuArray);

  return {
    currentData: [
      ['1.0 - 1.9 Stars', currentVolumeCounts.volumeLessThan2],
      ['2.0 - 2.9 Stars', currentVolumeCounts.volumeLessThan3],
      ['3.0 - 3.9 Stars', currentVolumeCounts.volumeLessThan4],
      ['4.0 - 5.0 Stars', currentVolumeCounts.volumeLessThan5]
    ],
    compareData: [
      ['1.0 - 1.9 Stars', compareVolumeCounts.volumeLessThan2],
      ['2.0 - 2.9 Stars', compareVolumeCounts.volumeLessThan3],
      ['3.0 - 3.9 Stars', compareVolumeCounts.volumeLessThan4],
      ['4.0 - 5.0 Stars', compareVolumeCounts.volumeLessThan5]
    ]
  };
};

// There is no way to backend to bucketing the filtered volume count, so we will manually add in frontend.
// This is also used by in both Ratings tab and Reviews tab Product Catalog Count by Cumulative.
export const useWeeklyProductCatalogCountData = ({
  fieldName,
  shouldStartFetch = false
}: {
  shouldStartFetch: boolean;
  fieldName: string;
}) => {
  const fieldMap = {
    childWeeklyRatingVolume: {
      displayName: 'Weekly Rating Volume',
      name: RatingsAndReviewMetric.WEEKLY_RATING_VOLUME,
      func: 'sum',
      export: false
    },
    weeklyReviewVolume: { displayName: 'Weekly Review Volume', name: 'weeklyReviewVolume', func: 'sum', export: false }
  };
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const { displayName, name, func, enableExport } = fieldMap[fieldName];
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const aggregationByWeeklyVolume = new AggregationBuilder('stacklineSku')
    .addAggregationField(displayName, name, func, enableExport)
    .setGroupByFieldName('stacklineSku')
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .build();

  const requestByWeeklyVolume = new AdvancedSearchRequestBuilder(
    `mainEntityMetrics-client-${beaconClientId}-productCatalogCount${name}-weekly`,
    `beacon-${BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2}`
  )
    .setPageNumber(1)
    .setPageSize(65000)
    .setRetailerId(retailer.id)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setSearchBy('parent')
    .addAggregation(aggregationByWeeklyVolume)
    .setReturnAdditionalMetadata(false)
    .addSortField(displayName, name, func, enableExport)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const comparisonAggregationBychildWeeklyRatingVolume = new AggregationBuilder('stacklineSku')
    .addAggregationField(displayName, name, func, enableExport)
    .setGroupByFieldName('stacklineSku')
    .addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek)
    .build();

  const comparisonRequestBychildWeeklyRatingVolume = requestByWeeklyVolume
    .clone()
    .clearAggregations()
    .addAggregation(comparisonAggregationBychildWeeklyRatingVolume)
    .clearConditionRangeFilters()
    .addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek);
  const requestArray = [requestByWeeklyVolume.build(), comparisonRequestBychildWeeklyRatingVolume.build()];

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: `productCatalogCount${name}-columnChart`,
    requestBody: requestArray,
    queryKeys: requestArray,
    shouldPerformFetch: shouldStartFetch
  });

  return { isLoading, data };
};

/**
 * Parse Product Catalog Count by Cumulative Rating Tier
 */
export const parseCumulativeProductCatalogCountRatingResponse = ({
  data,
  comparisonEndWeek
}: {
  data: ProductCatalogCountRatingVolumeResponse;
  comparisonEndWeek?: number;
}) => {
  const currentProductCatalogCountDataset = data?.data.map((datum, index) => {
    const currentProductCatalogCount =
      datum?.aggregations?.by_retailerId?.[0]?.additionalValues.stacklineSku_cardinality_count ?? 0;

    return [STAR_RATING_RANGE[index].displayName, currentProductCatalogCount];
  });

  const compareProductCatalogCountDataset = data?.data.map((datum, index) => {
    const compareProductCatalogCount =
      datum?.aggregations?.by_retailerId?.[0]?.additionalValues?.[
      `stacklineSku_cardinality_count_${comparisonEndWeek}_${comparisonEndWeek}`
      ] ?? 0;

    return [STAR_RATING_RANGE[index].displayName, compareProductCatalogCount];
  });

  return { currentData: currentProductCatalogCountDataset, compareData: compareProductCatalogCountDataset };
};

/**
 * Fetch Product Catalog Count by Cumulative Rating Tier
 */
export const useLifetimeProductCatalogCountRatingData = () => {
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);

  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);

  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const requestArray = STAR_RATING_RANGE.map(({ range: [min, max] }) => {
    const aggregationByLifeTimeRating = new AggregationBuilder('retailerId')
      .addAggregationField('Stackline SKU', 'stacklineSku', 'cardinality', false)
      .setSortDirection(null)
      .setGroupByFieldName('retailerId')
      .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
      .addComparisonRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek)
      .build();
    const requestByLifetimeRating = new AdvancedSearchRequestBuilder(
      `mainEntityMetrics-client-${beaconClientId}-productCatalogCountRatingData`,
      'beacon-ratings-reviews-metrics-v2'
    )
      .setPageNumber(1)
      .setPageSize(10000)
      .setReturnAdditionalMetadata(false)
      .setRetailerId(retailer.id)
      .setPeriod('year')
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .addConditionTermFilter('retailerId', 'should', [retailer.id])
      .setSearchBy('parent')
      .addAggregation(aggregationByLifeTimeRating)
      .addSortField('Stackline SKU', 'stacklineSku', 'cardinality', false)
      .apply(applyEntityFilters)
      .apply(removeRetailPriceRangeFilters);

    requestByLifetimeRating.addConditionRangeFilter('lifetimeRating', min, max);
    return requestByLifetimeRating.build();
  });

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: 'productCatalogCountRating-columnChart',
    requestBody: requestArray,
    queryKeys: requestArray
  });

  return { isLoading, data };
};

export const useWeeklyProductCatalogCountRatingData = ({ shouldStartFetch = false }: { shouldStartFetch: boolean }) => {
  const retailer = useAppSelector((state) => state.retailer);
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');

  const aggregationByWeeklyRating = new AggregationBuilder('stacklineSku')
    .addAggregationField('Weighted Weekly Rating', 'weightedWeeklyRating', 'sum', false)
    .addAggregationField('Weekly Rating Volume', 'childWeeklyRatingVolume', 'sum', false)
    .addAggregationField(
      'Weekly Rating',
      'weeklyRating',
      'computed',
      false,
      '(childWeeklyRatingVolume_sum > 0 ? weightedWeeklyRating_sum / childWeeklyRatingVolume_sum : 0)'
    )
    .setGroupByFieldName('stacklineSku')
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .addConditionRangeFilter('weeklyRating', 1, 5)
    .build();

  const requestByWeeklyRating = new AdvancedSearchRequestBuilder(
    `mainEntityMetrics-client-${beaconClientId}-productCatalogCountRatingData-weekly`,
    `beacon-${BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2}`
  )
    .setPageNumber(1)
    .setPageSize(65000)
    .setRetailerId(retailer.id)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setSearchBy('parent')
    .addAggregation(aggregationByWeeklyRating)
    .setReturnAdditionalMetadata(false)
    .addSortField('childWeeklyRatingVolume', 'childWeeklyRatingVolume', 'sum', false)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const comparisonAggregationByWeeklyRating = new AggregationBuilder('stacklineSku')
    .addAggregationField('Weighted Weekly Rating', 'weightedWeeklyRating', 'sum', false)
    .addAggregationField('Weekly Rating Volume', 'childWeeklyRatingVolume', 'sum', false)
    .addAggregationField(
      'Weekly Rating',
      'weeklyRating',
      'computed',
      false,
      '(childWeeklyRatingVolume_sum > 0 ? weightedWeeklyRating_sum / childWeeklyRatingVolume_sum : 0)'
    )
    .setGroupByFieldName('stacklineSku')
    .addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek)
    .addConditionRangeFilter('weeklyRating', 1, 5)
    .build();

  const comparisonRequestByWeeklyRating = requestByWeeklyRating
    .clone()
    .clearAggregations()
    .addAggregation(comparisonAggregationByWeeklyRating)
    .clearConditionRangeFilters()
    .addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek);
  const requestArray = [requestByWeeklyRating.build(), comparisonRequestByWeeklyRating.build()];

  const { isLoading, data } = useGenericAdvancedSearch({
    requestId: 'productCatalogCountWeeklyRating-columnChart',
    requestBody: requestArray,
    queryKeys: requestArray
  });

  return { isLoading, data };
};

/**
 * This request is used for checking if there is a value for each page (there are chance that they don't have any ratings or reviews)
 * Summary page will need both lifetimeReviewVolume and childLifetimeRatingVolume
 *  */

export const useReviewCumulativeValues = ({
  aggregationFieldNames
}: {
  aggregationFieldNames: AggregationFieldName[];
}) => {
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');
  const retailerId = useRetailerId();
  const reviewCountRequestId = 'reviewsCumulativeCount';

  const reviewCumulativeCountRequestBuilder = useAdvancedSearchRequestBuilder(
    reviewCountRequestId,
    'beacon-ratings-reviews-metrics-v2'
  );

  const aggregationBuilder = new AggregationBuilder('retailerId');

  aggregationFieldNames.forEach((fieldName) =>
    aggregationBuilder.addAggregationField(fieldName, fieldName, 'sum', false)
  );

  aggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);

  reviewCumulativeCountRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setReturnAdditionalMetadata(false)
    .setRetailerId(retailerId)
    .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .addSortField('childLifetimeRatingVolume', 'childLifetimeRatingVolume', 'sum', false)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const reviewCumulativeCountRequest = reviewCumulativeCountRequestBuilder.build();

  const { data, isLoading: reviewCumulativeCountLoading } = useGenericAdvancedSearch({
    requestId: reviewCountRequestId,
    queryKeys: [reviewCumulativeCountRequest],
    requestBody: [reviewCumulativeCountRequest]
  });

  const reviewCumulativeCountData = useMemo(() => {
    return data ? data.data[0].aggregations.by_retailerId : null;
  }, [data]);

  return { reviewCumulativeCountData, reviewCumulativeCountLoading };
};
